import { apiClient, BACKEND_URL } from '../../services/api';
import { compressEventImage } from '../../utils/compressEventImage';

const invitationApi = {
  // Organiser: create invitations for an event
  createInvitations: (data) =>
    apiClient.post('/invitations', data).then(r => r.data),

  // Organiser: list recipients in a campaign
  listInvitations: (campaignId) =>
    apiClient.get(`/invitations?campaign_id=${encodeURIComponent(campaignId)}`).then(r => r.data),

  // Organiser: all sent invitations across all events
  listSent: () =>
    apiClient.get('/invitations/sent').then(r => r.data),

  // Recipient: received invitations
  listReceived: () =>
    apiClient.get('/invitations/received').then(r => r.data),

  // Authenticated RSVP
  rsvp: (invitationId, status) =>
    apiClient.put(`/invitations/${invitationId}/rsvp`, { status }).then(r => r.data),

  // Public (email-link) RSVP
  rsvpPublic: (invitationId, status, token) =>
    apiClient.put(`/invite/public/${invitationId}/rsvp`, { status, token }).then(r => r.data),

  // Public view (no auth)
  getPublicInvitation: (invitationId, token) => {
    const params = token ? `?token=${encodeURIComponent(token)}` : '';
    return apiClient.get(`/invite/public/${invitationId}${params}`).then(r => r.data);
  },

  // Nudge (resend, 1/day)
  nudge: (invitationId) =>
    apiClient.post(`/invitations/${invitationId}/nudge`).then(r => r.data),

  // Chat (authenticated)
  getMessages: (invitationId) =>
    apiClient.get(`/invitations/${invitationId}/messages`).then(r => r.data),
  sendMessage: (invitationId, message_text) =>
    apiClient.post(`/invitations/${invitationId}/messages`, { message_text }).then(r => r.data),

  // Chat (token-based, no account required)
  getMessagesPublic: (invitationId, token) =>
    apiClient.get(`/invite/public/${invitationId}/messages?token=${encodeURIComponent(token)}`).then(r => r.data),
  sendMessagePublic: (invitationId, message_text, token) =>
    apiClient.post(`/invite/public/${invitationId}/messages`, { message_text, token }).then(r => r.data),

  // Token-based event view (gallery + my photos for non-CM invitees)
  getEventView: (invitationId, token) => {
    const params = token ? `?token=${encodeURIComponent(token)}` : '';
    return apiClient.get(`/invite/public/${invitationId}/event-view${params}`).then(r => r.data);
  },

  // Submit selfie for token-based invitee: upload presigned + create photo request
  submitInviteeSelfie: async (invitationId, token, eventId, selfieFile, consentGiven) => {
    const compressed = await compressEventImage(selfieFile);
    // Step 1: Get presigned upload URL (invitation_token auth)
    const uploadRes = await fetch(`${BACKEND_URL}/api/events/${eventId}/photo-requests/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        invitation_id: invitationId,
        invitation_token: token,
        file: { name: 'selfie.jpg', size: compressed.size, content_type: 'image/jpeg' },
      }),
    });
    const uploadData = await uploadRes.json();
    if (!uploadRes.ok) throw new Error(uploadData.error || 'Failed to prepare upload');
    // Step 2: Upload to S3
    const s3Res = await fetch(uploadData.upload.url, {
      method: 'PUT',
      headers: { 'Content-Type': 'image/jpeg' },
      body: compressed,
    });
    if (!s3Res.ok) throw new Error('Failed to upload selfie');
    // Step 3: Create photo request
    const reqRes = await fetch(`${BACKEND_URL}/api/events/${eventId}/photo-requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        invitation_id: invitationId,
        invitation_token: token,
        request_id: uploadData.request_id,
        reference_photo_key: uploadData.upload.key,
        consent_reference_face_match: consentGiven ? 'true' : '',
        skip_contact_required: true,
      }),
    });
    const reqData = await reqRes.json();
    if (!reqRes.ok) throw new Error(reqData.error || 'Failed to create photo request');
    return reqData;
  },

  // Cover image presigned upload
  getCoverUploadUrl: (eventId, invitationId) =>
    apiClient.post('/invitations/cover-upload-url', { event_id: eventId, invitation_id: invitationId }).then(r => r.data),

  // Update invitation message/cover
  updateInvitation: (invitationId, data) =>
    apiClient.put(`/invitations/${invitationId}`, data).then(r => r.data),

  // Bulk-update message+cover+name for all recipients in a campaign (organiser)
  saveCampaignDraft: (campaignId, data) =>
    apiClient.put(`/invitations/campaign/${encodeURIComponent(campaignId)}/draft`, data).then(r => r.data),

  // Delete / revoke an invitation (single recipient)
  deleteInvitation: (invitationId) =>
    apiClient.delete(`/invitations/${invitationId}`).then(r => r.data),

  // Delete an entire campaign
  deleteCampaign: (campaignId) =>
    apiClient.delete(`/invitations/campaign/${encodeURIComponent(campaignId)}`).then(r => r.data),
};

export default invitationApi;
